// @flow

// Module dependencies
import Logging from 'app/common/lib/Logging';

const stringLogger = Logging.getLogger('string');

export type InterpolationContext = {
  [key: string]: string,
};

/**
 * Perform string interpolation on a format string with Jinja-like substitution markers.
 *
 * @param {string} format
 * @param {!Object.<string,*>=} args A dict of marker id to value.
 * @return {string} The formatted string.
 */
const _format = function(re, format, args) {
  const replacer = function(match, argId) {
    // Traditionally, we've allowed HTML inside of substitution values, so don't HTML-escape.
    if (args && Object.prototype.hasOwnProperty.call(args, argId)) {
      return args[argId];
    }

    stringLogger.error('Missing arg for format string', { format, argId });
    return '';
  };

  return format.replace(re, replacer);
};

/**
 * Match Jinja substution markers in a string like "My name is {{ username }}".
 * @const
 * @type {RegExp}
 */
const JINJA_LIKE_SUBSTITUTION_MARKER = /\{\{\s*(\w+)\s*\}\}/g;

export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/\'/g, '&#39;');
}

/**
 * Perform string interpolation on a format string with Jinja-like substitution markers.
 *
 * @param {string} format
 * @param {!Object.<string,*>=} args A dict of marker id to value.
 * @return {string} The formatted string.
 */
export function dangerouslyFormat(format: string, args: InterpolationContext) {
  return _format(JINJA_LIKE_SUBSTITUTION_MARKER, format, args);
}

/**
 * Perform string interpolation like StringUtil.dangerouslyFormat but with HTML-escaped args.
 *
 * @param {string} format
 * @param {!Object.<string,*>=} args A dict of marker id to value.
 * @return {string} The formatted string.
 */
export function safeFormat(format: string, args: { +[key: string]: string | number }): string {
  const safeArgs: InterpolationContext = {};
  Object.keys(args).forEach((key) => {
    safeArgs[key] = args[key] ? escapeHtml(args[key].toString()) : '';
  });
  return dangerouslyFormat(format, (safeArgs: InterpolationContext));
}

export function format(format: string, args: Object) {
  throw new Error('StringUtil.format is deprecated. Use StringUtil.safeFormat instead.');
}

/**
 * Match substution markers in a an API template like "My name is { username }".
 * @const
 * @type {RegExp}
 */
const API_TEMPLATE_SUBSTITUTION_MARKER = /\{\s*(\w+)\s*\}/g;

/**
 * Perform string interpolation on a format string with Jinja-like substitution markers.
 *
 * @param {string} format
 * @param {!Object.<string,*>=} args A dict of marker id to value.
 * @return {string} The formatted string.
 */
export function formatApiTemplate(format: string, args: InterpolationContext) {
  return _format(API_TEMPLATE_SUBSTITUTION_MARKER, format, args);
}

/**
 * Regex for a valid email address.
 * from nunjucks/filters.js:urlize()
 */
const EMAIL_REGEX = /^[\w.!#$%&'*+\-\/=?\^`{|}~]+@[a-z\d\-]+(\.[a-z\d\-]+)+$/i;

/**
 * Test if a string is a valid email address
 */
export function isEmailAddress(value: string) {
  return EMAIL_REGEX.test(value);
}

/**
 * Return a capitalized string.
 */
export function capitalized(str: string) {
  return (
    Array.from(str)[0].toUpperCase() +
    Array.from(str)
      .slice(1)
      .join('')
  );
}

/**
 * Test if a string has no non-whitespace characters
 * @param {string} value: String to validate
 * @return {boolean} whether the string has no non-whitespace characters.
 */
export function isEmpty(value: string) {
  return value.trim().length === 0;
}

/**
 * Return a title-cased string.
 *
 * @param {string} str
 * @return {string} string with uppercased first character, lowercased remainder
 */
export function titleCased(str: string) {
  return str.replace(
    /([^\s:\-])([^\s:\-]*)/g,
    (substr) => substr.charAt(0).toUpperCase() + substr.substr(1).toLowerCase(),
  );
}

/**
 * Test whether a string begins with another string.
 * http://jsperf.com/regexp-test-vs-string-indexof-begins-with/4
 *
 * @param {string} haystack
 * @param {string} needle
 * @return {boolean} true if haystack begins with needle, otherwise false.
 */
export function beginsWith(haystack: string, needle: string) {
  return haystack.lastIndexOf(needle, 0) === 0;
}

/**
 * Test whether a string ends with another string.
 *
 * @param {string} haystack
 * @param {string} needle
 * @return {boolean} true if haystack ends with needle, otherwise false.
 */
export function endsWith(haystack: string, needle: string) {
  return haystack.indexOf(needle, haystack.length - needle.length) !== -1;
}

/**
 * @param {string} html
 * @return {string}
 */
export function stripTags(html: string) {
  return html.replace(/(<([^>]+)>)/gi, '');
}
